import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import { Separator } from "@arcadeai/design-system/components/ui/atoms/separator";
import { Form } from "@arcadeai/design-system/rhf";
import { ChevronDown, Loader2 } from "lucide-react";
import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import {
  DEFAULT_OIDC_USER_SOURCE_SCOPES,
  DEFAULT_OIDC_USER_SOURCE_SUBJECT_CLAIM,
  type UserSourceCreateRequest,
} from "../../types";
import { ClientIdFormField } from "./client-id-form-field";
import { ClientSecretFormField } from "./client-secret-form-field";
import { DescriptionFormField } from "./description-form-field";
import { IssuerFormField } from "./issuer-form-field";
import { NameFormField } from "./name-form-field";
import { ScopesFormField } from "./scopes-form-field";
import { SubjectClaimFormField } from "./subject-claim-form-field";

type UserSourceFormProps = {
  form: UseFormReturn<UserSourceCreateRequest>;
  onSubmit: (values: UserSourceCreateRequest) => void;
  onError: () => void;
  isPending: boolean;
  isEditing: boolean;
};

function hasCustomAdvancedValues(values: UserSourceCreateRequest): boolean {
  if (values.subject_claim !== DEFAULT_OIDC_USER_SOURCE_SUBJECT_CLAIM) {
    return true;
  }
  if (values.scopes.length !== DEFAULT_OIDC_USER_SOURCE_SCOPES.length) {
    return true;
  }
  return values.scopes.some(
    (scope, index) => scope !== DEFAULT_OIDC_USER_SOURCE_SCOPES[index]
  );
}

export function UserSourceForm({
  form,
  onSubmit,
  onError,
  isPending,
  isEditing,
}: UserSourceFormProps) {
  const [advancedOpen, setAdvancedOpen] = useState(() =>
    hasCustomAdvancedValues(form.getValues())
  );

  return (
    <Form {...form}>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit(onSubmit, onError)}
      >
        <NameFormField autoFocus control={form.control} />
        <DescriptionFormField control={form.control} />

        <Separator />

        <IssuerFormField control={form.control} />
        <ClientIdFormField control={form.control} />
        <ClientSecretFormField
          control={form.control}
          isEditing={isEditing}
          name="client_secret"
        />

        <Collapsible onOpenChange={setAdvancedOpen} open={advancedOpen}>
          <div className="rounded-lg border border-border">
            <CollapsibleTrigger
              render={
                <button
                  className="flex w-full cursor-pointer items-center justify-between rounded-lg bg-transparent p-4 text-left transition-colors hover:bg-accent"
                  type="button"
                />
              }
            >
              <div>
                <span className="font-medium text-sm">Advanced</span>
                <p className="text-muted-foreground text-xs">
                  Subject claim, scopes
                </p>
              </div>
              <ChevronDown
                className={`size-4 shrink-0 text-muted-foreground transition-transform ${
                  advancedOpen ? "rotate-180" : ""
                }`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent keepMounted>
              <div className="space-y-4 border-border border-t p-4">
                <SubjectClaimFormField control={form.control} />
                <ScopesFormField control={form.control} />
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        <Button className="mt-2 w-full" disabled={isPending} type="submit">
          {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
          <span>{isEditing ? "Update user source" : "Create user source"}</span>
        </Button>
      </form>
    </Form>
  );
}
